import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';

import { UitkDateRange } from 'uitk-react-date-range';
import { UitkDateRangeResult } from 'uitk-react-date-range/dist/types';
import { Level, liveAnnounce } from 'uitk-react-utils';
import { FlightsFlexSearchResults } from '__generated__/api';

import {
  analyticsConfigMapper,
  isFlexibleSearchCellSelected,
  getFlexibleSearchCellTextTheme,
  FlexSearchPerformance,
  getValueText,
  trackAnalytics
} from 'utility';
import { useStores } from 'utility/hooks';

export const FlexibleSearch = observer(() => {
  const {
    analytics,
    context,
    flexibleSearchResultsStore,
    routeStore,
    searchCriteriaStore,
    searchFormStore,
    sortFilterStore,
    cheapestListingInfoStore
  } = useStores();
  const { coverageAnalytics, resultsGrid } = flexibleSearchResultsStore;

  if (!resultsGrid) {
    return null;
  }

  const results = resultsGrid.results && (resultsGrid.results[0] as FlightsFlexSearchResults.Fragment[]);

  const transformedResults: UitkDateRangeResult[] = results.map(result => {
    const { labelText, nextSearchCriteria, selectAnalytics, theme, valueText, accessibility } = result;
    /* covered by automation */
    /* istanbul ignore next */
    const callback = useCallback(() => {
      searchCriteriaStore.syncWithNextSearchCriteria(nextSearchCriteria);
      searchCriteriaStore.clearSortFilterSearchCriteria();
      searchCriteriaStore.clearJourneysContinuationId();
      searchCriteriaStore.clearPagination();
      searchFormStore.syncJourneysWithSearchCriteriaStore(searchCriteriaStore);
      sortFilterStore.loading = true;
      searchCriteriaStore.triggerSearch(routeStore, context);
      liveAnnounce(accessibility.selectionDescription, Level.POLITE);
    }, [accessibility, nextSearchCriteria]);

    return {
      analytics: selectAnalytics && analyticsConfigMapper(selectAnalytics, analytics),
      dateText: labelText,
      onClickCallback: callback,
      selected: isFlexibleSearchCellSelected(theme),
      text: getValueText(cheapestListingInfoStore, valueText, theme, analytics),
      textTheme: getFlexibleSearchCellTextTheme(theme),
      accessibilityMessage: accessibility && accessibility.highlightDescription
    };
  });

  useEffect(() => {
    performance.mark(FlexSearchPerformance.RENDER_FLEX_SEARCH_END_MARK);
    performance.measure(
      FlexSearchPerformance.RENDER_FLEX_SEARCH_DURATION,
      FlexSearchPerformance.RENDER_FLEX_SEARCH_START_MARK,
      FlexSearchPerformance.RENDER_FLEX_SEARCH_END_MARK
    );
    trackAnalytics(coverageAnalytics, analytics);
  });

  return (
    <>
      <div data-test-id="flexible-search-container" className="side-spacing">
        <UitkDateRange loading={flexibleSearchResultsStore?.loading} results={transformedResults} />
      </div>
      <hr className="all-y-margin-six" aria-hidden="true" />
    </>
  );
});
